import { getProductImageURL } from '@noths/polaris-client-utils';
import type { ProductCarouselItem } from '@noths/polaris-design-system-candidate-components';
import type { Product } from '@noths/polaris-design-system-ooux-interfaces';
import type { CurrencyCode } from '@noths/polaris-dev-ts-types';

import type { CatalogServiceProductData } from 'src/services/catalog/types';

/**
 * Adapts a catalog response into the OOUX `Product` the design system consumes.
 *
 * The interface is deliberately semantic, so several catalog fields have no
 * counterpart and are not mapped: `salePercentage` because `ProductCard` derives
 * the discount by comparing `price` with `originalPrice`, `onSale` because the
 * discount is the pre-sale price being present, and `isNew` because it is card
 * presentation rather than a property of the product (see
 * `transformProductModelToProductCarouselItem`).
 */
export const transformProductModelToProduct = (
  { code, images, links, partner, preSalePrices, prices, title }: CatalogServiceProductData,
  { currency }: { currency: CurrencyCode },
): Product => {
  const price = prices.find((price) => price.currency === currency);
  const preSalePrice = preSalePrices?.find((price) => price.currency === currency);
  const [image] = images;

  return {
    // Non-purchasable products are filtered out before they reach here, so
    // everything the carousel renders is in stock.
    availability: 'in_stock',
    code: String(code),
    currencyCode: currency,
    /* The catalog returns a `{size}` token in place of a preset size, so the URL
       has to be resolved here: `ProductMedia.url` is contracted as a fully
       resolved URL and the card renders it verbatim, unlike the Ribbons card it
       replaces, which resolved the token itself. 'normal' is the size that card
       asked for, so the bytes on the wire do not change.

       Empty alt because the card wraps the image in a link named by the title,
       so describing it again would announce the product twice. */
    media: image ? [{ alt: '', type: 'image', url: getProductImageURL(image.href, 'normal') }] : [],
    /* Keyed on the pre-sale price alone, not on `onSale`. That matches the card
       this replaces, which took the pre-sale price and never received `onSale` at
       all, so a product priced down without the flag set keeps showing its
       discount. `onSale` still drives tracking, which reads the catalog product
       directly. */
    ...(preSalePrice && { originalPrice: preSalePrice.amount }),
    // The card shows the partner's name as plain text. `shortcode` and `url` are
    // optional and unrendered, so they are left out until something needs them.
    partner: { name: partner.name },
    price: price ? price.amount : 0,
    title,
    url: links[0]?.href || '',
  };
};

/**
 * One carousel item is one card's props: the product itself plus the flags that
 * are presentation rather than properties of the product.
 */
export const transformProductModelToProductCarouselItem = (
  product: CatalogServiceProductData,
  { currency, priority }: { currency: CurrencyCode; priority: boolean },
): ProductCarouselItem => ({
  isNew: product.isNew,
  priority,
  product: transformProductModelToProduct(product, { currency }),
  /* Constant, not `product.onSale`. Every discount the catalog expresses is a
     price reduction rather than a code-based offer, and the card this replaces
     styled all of them as a sale, so there is no per-product decision to make
     here. `promoteAsSale` does nothing on a product with no discount, so it does
     not need guarding. */
  promoteAsSale: true,
});
